/**
 * Integration with Gravity Forms' product/total system.
 *
 * Since GF 3.0 the Total field is driven by a global product state that GF
 * rebuilds from the DOM whenever any product field changes. GF identifies each
 * product by the field's input type, and the Booking field is a product field
 * with a custom input type of `gpb_booking`, which GF's parser has no case for.
 * GF therefore drops the booking from the rebuilt state, and the booking's price
 * disappears from the Total as soon as another product field is touched.
 *
 * `gform/products/pre_product_change` runs after GF has parsed the form and
 * before the result is written to the global state, so re-inserting the booking
 * there keeps its price in the Total no matter what triggered the rebuild.
 */

import { toNumber } from '../../helpers/currency';

interface GFOption {
	id: string;
	name: string;
	price: number;
	field_label?: string;
}

interface GFProduct {
	id: string;
	name: string;
	price: number;
	quantity: number;
	options: GFOption[];
	repeaterPath: string;
}

interface PreProductChangeData {
	formId: number | string;
	products: { products: GFProduct[] };
}

interface GFParser {
	getFieldId: (field: Element) => string;
	getProductMeta: (form: Element, field: Element) => GFOption[];
	getProductQuantity: (form: Element, field: Element) => number;
	getOptionFields: (form: Element, field: Element) => Element[];
	getFieldLabel: (field: Element) => string;
}

/**
 * GF's frontend globals, narrowed to the parts we use. `parser` ships in the
 * same module as the filter below, so it is always present once the filter runs.
 * `repeater` is only defined for forms that actually contain one.
 */
function gf() {
	return window.gform as unknown as {
		utils?: {
			addFilter?: (
				name: string,
				cb: (data: PreProductChangeData) => PreProductChangeData
			) => void;
		};
		products?: {
			updateProducts?: (formId: number | string) => void;
			parser: GFParser;
		};
		repeater?: { getRepeaterPath: (element: Element) => string };
	};
}

/**
 * Collects the options mapped to a booking field, the way GF collects them for
 * its own product fields.
 */
function getMappedOptions(
	form: HTMLElement,
	field: HTMLElement,
	parser: GFParser
): GFOption[] {
	const options: GFOption[] = [];

	parser.getOptionFields(form, field).forEach((optionField) => {
		parser.getProductMeta(form, optionField).forEach((option) => {
			options.push({
				...option,
				field_label: parser.getFieldLabel(optionField),
			});
		});
	});

	return options;
}

/**
 * Builds the product entry GF would have built for a booking field if it
 * understood the `gpb_booking` input type, reading the same `.1` and `.2` inputs
 * GF reads for a Single Product field.
 */
function buildBookingProduct(
	form: HTMLElement,
	field: HTMLElement,
	parser: GFParser
): GFProduct | null {
	// Mirrors GF's own check so a booking hidden by conditional logic, or sitting
	// on a hidden page, is not charged for.
	const page = field.closest('.gform_page') as HTMLElement | null;
	if (
		field.dataset.conditionalLogic === 'hidden' ||
		page?.dataset.conditionalLogic === 'hidden'
	) {
		return null;
	}

	const fieldId = parser.getFieldId(field);
	const priceInput = field.querySelector(
		`input[name^="input_${fieldId}.2"]`
	) as HTMLInputElement | null;

	if (!priceInput) {
		return null;
	}

	const nameInput = field.querySelector(
		`input[name^="input_${fieldId}.1"]`
	) as HTMLInputElement | null;

	return {
		id: fieldId,
		name: nameInput?.value ?? '',
		price: toNumber(priceInput.value || '0'),
		quantity: parser.getProductQuantity(form, field),
		options: getMappedOptions(form, field, parser),
		// GF's total calculation reads this unconditionally, so it must always be
		// a string. No repeater on the form means an empty path, which is correct.
		repeaterPath: gf().repeater?.getRepeaterPath(field) ?? '',
	};
}

let registered = false;

/**
 * Registers the product filter. Safe to call repeatedly; only the first call
 * registers. A no-op before GF 3.0, where the filter does not exist.
 */
export function registerBookingProductFilter(): void {
	const addFilter = gf().utils?.addFilter;

	if (registered || typeof addFilter !== 'function') {
		return;
	}

	registered = true;

	addFilter('gform/products/pre_product_change', (data) => {
		const form = document.getElementById(`gform_${data.formId}`);
		const parser = gf().products?.parser;

		if (!form || !parser) {
			return data;
		}

		form.querySelectorAll<HTMLElement>(
			'.gfield--input-type-gpb_booking'
		).forEach((field) => {
			if (field.closest('.gfield_repeater_template')) {
				return;
			}

			const product = buildBookingProduct(form, field, parser);

			if (product) {
				data.products.products.push(product);
			}
		});

		return data;
	});
}

/**
 * Asks GF to recalculate the form's totals after the booking price changed.
 *
 * The booking's price input is hidden, so the change events GF listens for never
 * reach it and GF has to be told explicitly. On GF 3.0+ that means rebuilding
 * the product state, which runs the filter above; older versions only have the
 * legacy total calculation.
 */
export function refreshGFProducts(formId: number): void {
	const updateProducts = gf().products?.updateProducts;

	if (typeof updateProducts === 'function') {
		updateProducts(formId);
		return;
	}

	window.gformCalculateTotalPrice?.(formId);
}
